import React from "react";
import { Tooltip } from "@sscs/ui";
import { PackagesReleaseStatusCount, ReleaseStatusCount } from "../../../types";
import { getTotalReleaseStatusCount } from "../../../utils";
import styles from "./summary.module.scss";

function isPackagesReleaseStatusCount(
  count: PackagesReleaseStatusCount | ReleaseStatusCount,
): count is PackagesReleaseStatusCount {
  return (count as PackagesReleaseStatusCount).none !== undefined;
}

type ReleaseStatusCountProps = {
  label: React.ReactNode;
  count: PackagesReleaseStatusCount | ReleaseStatusCount;
  className?: string;
};

const ReleaseStatusCount = ({
  label,
  count,
  className,
}: ReleaseStatusCountProps) => {
  const { pass, fail } = count;
  const none = isPackagesReleaseStatusCount(count) ? count.none : 0;
  const total = getTotalReleaseStatusCount(count);

  const failPercentage = (fail * 100) / total;
  const passPercentage = (pass * 100) / total;
  const nonePercentage = ((none ?? 0) * 100) / total;

  return (
    <div className={className}>
      <div className={styles["bar-label"]}>
        <div>{label}</div>
        <div className={styles["pass-fail-text"]}>
          Fail {fail.toLocaleString()} &middot; Pass {pass.toLocaleString()}
          {none > 0 && <> &middot; Not released {none.toLocaleString()}</>}
        </div>
      </div>
      <div className={styles["percentage-bars"]}>
        {failPercentage > 0 && (
          <Tooltip
            trigger="graphic"
            title={`Fail: ${failPercentage.toFixed(2)}%`}
          >
            <div
              style={{ width: `${failPercentage}%`, minWidth: "20px" }}
              className={styles.fail}
            />
          </Tooltip>
        )}
        {passPercentage > 0 && (
          <Tooltip
            trigger="graphic"
            title={`Pass: ${passPercentage.toFixed(2)}%`}
          >
            <div
              style={{ width: `${passPercentage}%`, minWidth: "20px" }}
              className={styles.pass}
            />
          </Tooltip>
        )}
        {nonePercentage > 0 && (
          <Tooltip
            trigger="graphic"
            title={`Not released: ${nonePercentage.toFixed(2)}%`}
          >
            <div
              style={{ width: `${nonePercentage}%`, minWidth: "20px" }}
              className={styles.none}
            />
          </Tooltip>
        )}
      </div>
    </div>
  );
};

export default ReleaseStatusCount;
